import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../state/hooks';
import { omapApi } from '../../services/omapApi';
import './DossierViewerTab.css';

const PILLARS = ['financial', 'customer', 'process', 'learning_growth'];

interface BriefingNarrative {
  opening?: string;
  whats_working?: string;
  needs_attention?: string;
  focus_next?: string;
}

const DossierViewerTab: React.FC = () => {
  const { activeCompanyId } = useAppSelector((state) => state.companies);
  const companyId = activeCompanyId ? parseInt(activeCompanyId.replace('company_', '')) : null;
  const [dossierIndex, setDossierIndex] = useState<any>(null);
  const [briefingNarrative, setBriefingNarrative] = useState<BriefingNarrative>({});
  const [selectedPillar, setSelectedPillar] = useState<string | null>(null);
  const [pillarDetail, setPillarDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadDossier = (cId: number) => {
    setLoading(true);
    omapApi.getDossierIndex(cId)
      .then((data) => {
        if (data?.status === 'no_dossier') {
          setDossierIndex(null);
          setBriefingNarrative({});
        } else {
          setDossierIndex(data);
          setBriefingNarrative(data?.briefing_narrative || {});
        }
      })
      .catch(() => {
        setDossierIndex(null);
        setBriefingNarrative({});
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (companyId) loadDossier(companyId);
  }, [companyId]);

  useEffect(() => {
    if (companyId && selectedPillar) {
      omapApi.getDossierPillar(companyId, selectedPillar)
        .then(setPillarDetail)
        .catch(() => setPillarDetail(null));
    } else {
      setPillarDetail(null);
    }
  }, [companyId, selectedPillar]);

  const handleGenerate = async () => {
    if (!companyId || generating) return;
    setGenerating(true);
    try {
      await omapApi.triggerConsolidation(companyId);
      // Reload dossier to get fresh data
      loadDossier(companyId);
    } catch (err) {
      console.error('Consolidation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  if (!companyId) {
    return <div className="dossier-empty">Select a company to view its Intelligence Dossier.</div>;
  }

  if (loading) return <div className="dossier-loading">Loading dossier...</div>;

  const hasNarrative = briefingNarrative.opening || briefingNarrative.whats_working
    || briefingNarrative.needs_attention || briefingNarrative.focus_next;

  const summaries = dossierIndex?.pillar_summaries || {};
  const themes = dossierIndex?.active_themes || [];
  const contradictions = dossierIndex?.unresolved_contradictions || [];

  return (
    <div className="dossier-viewer">
      <div className="dossier-header">
        <h3>Company Intelligence Dossier</h3>
        <button
          className="generate-briefing-btn"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Generating briefing...' : (hasNarrative ? 'Refresh Briefing' : 'Generate Briefing')}
        </button>
      </div>

      {dossierIndex?.last_consolidated && (
        <div className="dossier-meta">Last consolidated: {new Date(dossierIndex.last_consolidated).toLocaleString()}</div>
      )}

      {/* Briefing Narrative Section */}
      {hasNarrative ? (
        <div className="briefing-section">
          {briefingNarrative.opening && (
            <p className="briefing-opening">{briefingNarrative.opening}</p>
          )}
          {briefingNarrative.whats_working && (
            <div className="briefing-block briefing-whats-working">
              <div className="briefing-label">What's Working</div>
              <p>{briefingNarrative.whats_working}</p>
            </div>
          )}
          {briefingNarrative.needs_attention && (
            <div className="briefing-block briefing-needs-attention">
              <div className="briefing-label">What Needs Attention</div>
              <p>{briefingNarrative.needs_attention}</p>
            </div>
          )}
          {briefingNarrative.focus_next && (
            <div className="briefing-block briefing-focus-next">
              <div className="briefing-label">Recommended Focus</div>
              <p>{briefingNarrative.focus_next}</p>
            </div>
          )}
        </div>
      ) : !dossierIndex ? (
        <div className="briefing-empty">
          No briefing available yet. Click "Generate Briefing" to create your first company intelligence briefing.
        </div>
      ) : null}

      {themes.length > 0 && (
        <div className="dossier-themes">
          <strong>Active Themes:</strong> {themes.join(', ')}
        </div>
      )}

      <div className="dossier-pillars">
        {PILLARS.map((pillar) => (
          <div
            key={pillar}
            className={`dossier-pillar-card ${selectedPillar === pillar ? 'selected' : ''}`}
            onClick={() => setSelectedPillar(selectedPillar === pillar ? null : pillar)}
          >
            <div className="pillar-name">{pillar.replace('_', ' & ').replace(/\b\w/g, c => c.toUpperCase())}</div>
            <div className="pillar-summary">{summaries[pillar] || 'No data.'}</div>
          </div>
        ))}
      </div>

      {selectedPillar && pillarDetail && (
        <div className="dossier-detail">
          <h4>{selectedPillar.replace('_', ' & ').replace(/\b\w/g, c => c.toUpperCase())} Detail</h4>
          <pre className="detail-json">{JSON.stringify(pillarDetail, null, 2)}</pre>
        </div>
      )}

      {contradictions.length > 0 && (
        <div className="dossier-contradictions">
          <h4>Unresolved Contradictions</h4>
          {contradictions.map((c: any, i: number) => (
            <div key={i} className="contradiction-item">
              <strong>{c.topic}:</strong> {c.old_view} vs. {c.new_view}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DossierViewerTab;
